import * as React from 'react';
import { cn } from '@/lib/utils';
import { fieldChromeWidth, fieldChromeWidthAttributes } from '@/lib/density';
import { hasFieldChrome } from '@/lib/fieldChrome';
import { Label } from '@/components/ui/label';
import styles from './input.module.css';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  /** Opt-in minute granularity for datetime-local/time inputs (sets `step` in seconds). */
  minuteStep?: number;
  error?: React.ReactNode;
  helperText?: React.ReactNode;
  label?: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

/** Right keyboard on iOS/Android. Callers can still pass an explicit inputMode. */
export function inputModeForType(
  type: React.HTMLInputTypeAttribute | undefined,
): React.HTMLAttributes<HTMLInputElement>['inputMode'] {
  switch (type) {
    case 'email':
      return 'email';
    case 'tel':
      return 'tel';
    case 'url':
      return 'url';
    case 'number':
      return 'numeric';
    case 'search':
      return 'search';
    default:
      return undefined;
  }
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    allProps,
    ref,
  ) => {
    const {
      'aria-describedby': ariaDescribedby,
      'aria-invalid': ariaInvalid,
      className,
      error,
      helperText,
      id,
      label,
      prefix,
      required,
      suffix,
      type,
      style,
      inputMode,
      minuteStep,
      placeholder,
      readOnly,
      ...props
    } = allProps;
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const helperTextId = helperText ? `${inputId}-helper` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;
    const describedBy = [ariaDescribedby, helperTextId, errorId].filter(Boolean).join(' ') || undefined;
    const isDateInput =
      type === 'date' || type === 'datetime-local' || type === 'time' || type === 'month';
    const isFieldLayout = hasFieldChrome(allProps, label, helperText);
    const hasSuffix = suffix != null && suffix !== false;
    const isDecorativeSuffix = typeof suffix === 'string' || typeof suffix === 'number';
    const chromeWidth = fieldChromeWidth(className);
    const chromeWidthAttributes = fieldChromeWidthAttributes(chromeWidth);
    const stretchWrapper =
      isFieldLayout || /\bw-full\b/.test(className ?? '') || /\bflex-1\b/.test(className ?? '');
    const fillContainer = stretchWrapper && chromeWidth.mode !== 'shrink';
    const needsRelativeWrapper = Boolean(prefix || hasSuffix || isDateInput);
    // A read-only date shows its value as plain YYYY-MM-DD text: no picker icon, and no
    // native picker on phones that ignore `readonly` on date inputs.
    const renderedType = readOnly && type === 'date' ? 'text' : type;

    const inputEl = (
      <input
        id={inputId}
        type={renderedType}
        inputMode={readOnly ? undefined : inputMode ?? inputModeForType(type)}
        required={required}
        readOnly={readOnly}
        // A placeholder in a locked field reads like a value; show only real values.
        placeholder={readOnly ? undefined : placeholder}
        step={minuteStep ? minuteStep * 60 : undefined}
        aria-describedby={describedBy}
        aria-invalid={ariaInvalid ?? (error ? true : undefined)}
        className={cn(
          styles.root,
          'text-body-m',
          prefix && styles.hasPrefix,
          hasSuffix && (isDecorativeSuffix ? styles.suffixPadText : styles.suffixPadNode),
          (isFieldLayout || fillContainer) && styles.full,
          isDateInput && styles.date,
          fillContainer && isDateInput && styles.dateFill,
          className,
        )}
        style={{
          ...style,
          ...(fillContainer && isDateInput
            ? { width: '100%', minWidth: '100%', boxSizing: 'border-box' }
            : {}),
        }}
        ref={ref}
        {...props}
      />
    );

    const control = needsRelativeWrapper ? (
      <div
        className={cn(
          styles.affixWrap,
          fillContainer || isFieldLayout ? styles.affixWrapFill : styles.affixWrapFit,
        )}
      >
        {prefix && (
          <span className={styles.prefix} aria-hidden="true">
            {prefix}
          </span>
        )}
        {inputEl}
        {hasSuffix ? (
          <div className={cn(styles.suffix, isDecorativeSuffix && styles.suffixTextWrap)}>
            {isDecorativeSuffix ? (
              <span className={styles.suffixText} aria-hidden="true">
                {suffix}
              </span>
            ) : (
              suffix
            )}
          </div>
        ) : null}
      </div>
    ) : inputEl;

    const shrinkWrap = chromeWidth.mode === 'shrink';
    const chromeClass = stretchWrapper ? (shrinkWrap ? styles.chromeFull : styles.chromeSized) : styles.chromeInline;
    const wrappedControl = (
      <div
        className={cn(styles.chrome, chromeClass)}
        {...(stretchWrapper && !shrinkWrap ? chromeWidthAttributes : {})}
      >
        {control}
      </div>
    );

    if (!isFieldLayout) {
      return wrappedControl;
    }

    return (
      <div className={styles.field} {...chromeWidthAttributes}>
        {label && (
          <Label htmlFor={inputId}>
            {label}
            {required && <span aria-hidden="true" className={styles.required}>*</span>}
          </Label>
        )}
        {wrappedControl}
        {helperText && (
          <p id={helperTextId} className={styles.helper}>
            {helperText}
          </p>
        )}
        {error && (
          <p id={errorId} role="alert" className={styles.error}>
            {error}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
